// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DEFAULT_UNIT_OF_MEASURE } from '@/constants/unit-of-measure';
import { getItems } from '@/items/api/get-items';
import {
  BOMItemAutocomplete,
  CREATE_ITEM_OPTION_ID,
} from '@/items/components/bom-item-autocomplete';
import { SparseItem } from '@/sparse/api/types';
import { FilterOptionsState, createFilterOptions } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { ForwardedRef, forwardRef } from 'react';

type BOMItemFilterCallback = (options: SparseItem[]) => SparseItem[];

const filter = createFilterOptions<SparseItem>();

/**
 * Compares two items to be sorted as search results.
 * Items are sorted by name.
 */
function compareItems(item: SparseItem, other: SparseItem) {
  return item.name.localeCompare(other.name);
}

/**
 * Filters the item options based on the provided filter callback and hidden items.
 * Archived items are always excluded.
 *
 * @param items - The full list of items to filter
 * @param filterCallback - Optional callback to apply custom filtering logic
 * @param hiddenItems - Items to exclude from the dropdown (e.g., items already
 *   selected in a BOM to prevent duplicates)
 */
function getOptions(
  items: SparseItem[] = [],
  filterCallback: BOMItemFilterCallback | undefined,
  hiddenItems: SparseItem[] | undefined
) {
  let filteredItems = items.filter((item) => !item.isArchived);

  if (hiddenItems && hiddenItems.length > 0) {
    const hiddenItemIds = new Set(hiddenItems.map((item) => item.id));
    filteredItems = filteredItems.filter((item) => !hiddenItemIds.has(item.id));
  }

  if (filterCallback === undefined) {
    return filteredItems;
  }

  return filterCallback(filteredItems);
}

interface Props {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If true, the input is disabled.
   */
  disabled?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * If provided, this function will be used to filter the item options.
   */
  filter?: BOMItemFilterCallback;

  /**
   * Items to exclude from the dropdown options.
   * Used in BOM editing to hide items that are already selected in other lines,
   * preventing duplicate items in the same bill of materials.
   */
  hiddenItems?: SparseItem[];

  /**
   * The currently selected item.
   */
  value: SparseItem | null;

  /**
   * A callback fired when the selected item changes.
   */
  onChange: (value: SparseItem | null) => void;

  fullWidth?: boolean;

  placeholderText?: string;

  showSearchAdornment?: boolean;
}

/**
 * Opens the items page in a new tab with a "new" query parameter.
 * This triggers the "Add item" dialog to open with the item name pre-populated.
 */
function openAddItemPage(name: string) {
  const url = new URL('/catalog/items', document.location.origin);

  url.searchParams.append('new', encodeURIComponent(name));

  window.open(url, '_blank');
}

/**
 * Renders a BOM item autocomplete component with options from the remote API.
 * Fetches only BULK items since only bulk items can be added to a BOM.
 */
function BOMItemSearchRenderer(
  props: Props,
  ref: ForwardedRef<HTMLInputElement>
) {
  const { data, isLoading } = useQuery({
    queryKey: ['item', 'bom-items'],
    queryFn: () => getItems({ type: 'BULK', limit: 1000 }),
  });

  // Map the API response to SparseItem format
  // BOM items are always BULK type (physical, no assets); shouldRenderSerialFields comes from the SKU's display flag.
  const bomItems: SparseItem[] =
    data?.results.map((item) => ({
      id: item.id,
      key: item.key,
      name: item.name,
      type: 'BULK',
      sku: item.sku,
      isArchived: item.isArchived,
      unitOfMeasure: DEFAULT_UNIT_OF_MEASURE,
      hasAssets: false,
      isPhysical: true,
      shouldRenderSerialFields: item.shouldRenderSerialFields,
    })) ?? [];

  const items = getOptions(bomItems, props.filter, props.hiddenItems)
    .slice()
    .sort(compareItems);

  const filterOptions = (
    options: SparseItem[],
    state: FilterOptionsState<SparseItem>
  ) => {
    const filtered = filter(options, state);
    const inputValue = state.inputValue.trim();

    // Add create new option if there's input and no exact match
    if (
      inputValue &&
      !filtered.some(
        (item) => item.name.toLowerCase() === inputValue.toLowerCase()
      )
    ) {
      filtered.push({
        id: CREATE_ITEM_OPTION_ID,
        key: CREATE_ITEM_OPTION_ID,
        name: inputValue,
        type: 'BULK',
        sku: '',
        isArchived: false,
        unitOfMeasure: DEFAULT_UNIT_OF_MEASURE,
        hasAssets: false,
        isPhysical: true,
        shouldRenderSerialFields: false,
      });
    }

    return filtered;
  };

  const handleChange = (_: unknown, value: SparseItem | string | null) => {
    if (typeof value === 'string') {
      return openAddItemPage(value);
    }

    if (value === null) {
      return props.onChange(null);
    }

    if (value.id === CREATE_ITEM_OPTION_ID) {
      openAddItemPage(value.name);
      return;
    }

    props.onChange(value);
  };

  return (
    <BOMItemAutocomplete
      ref={ref}
      size="small"
      name={props.name}
      value={props.value}
      error={props.error}
      label={props.label}
      options={items}
      loading={isLoading}
      onChange={handleChange}
      disabled={props.disabled}
      helperText={props.helperText}
      filterOptions={filterOptions}
      loadingText="Loading..."
      fullWidth={props.fullWidth}
      placeholder={props.placeholderText}
      showSearchAdornment={props.showSearchAdornment}
    />
  );
}

/**
 * A controlled component for selecting a BOM item from a list of bulk items
 * from the remote API. Only BULK type items are shown since only bulk items
 * can be added to a Bill of Materials.
 */
export const BOMItemSearch = forwardRef(BOMItemSearchRenderer);
